import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'

// Framer-aware React Router Link
const MotionLink = motion(Link)

// ─── Style maps ───────────────────────────────────────────────────────────────
const VARIANTS = {
  primary: [
    'bg-primary text-white',
    'hover:bg-primary-dark hover:shadow-lg hover:shadow-primary/40',
  ],
  secondary: [
    'border-2 border-primary text-primary bg-transparent',
    'hover:bg-primary hover:text-white hover:shadow-lg hover:shadow-primary/30',
  ],
  accent: [
    'bg-accent text-white',
    'hover:bg-accent-dark hover:shadow-lg hover:shadow-accent/30',
  ],
  whatsapp: [
    'bg-green-500 text-white',
    'hover:bg-green-600 hover:shadow-lg hover:shadow-green-500/30',
  ],
  phone: [
    'bg-secondary text-ink font-black',
    'hover:bg-secondary-dark hover:shadow-lg hover:shadow-secondary/30',
  ],
  ghost: [
    'text-primary bg-transparent',
    'hover:bg-primary/10 hover:text-primary-dark',
  ],
  'outline-white': [
    'border-2 border-white text-white bg-transparent',
    'hover:bg-white hover:text-primary',
  ],
  dark: [
    'bg-dark text-white',
    'hover:bg-dark-800 hover:shadow-lg hover:shadow-dark/30',
  ],
  warm: [
    'bg-gradient-warm text-white',
    'hover:opacity-90 hover:shadow-lg',
  ],
}

const SIZES = {
  sm: 'px-6  py-2   text-sm',
  md: 'px-8  py-3   text-base',
  lg: 'px-10 py-4   text-lg',
  xl: 'px-12 py-5   text-xl',
}

const BASE = [
  'inline-flex items-center justify-center gap-2',
  'font-body font-bold rounded-full',
  'transition-all duration-300 cursor-pointer select-none',
  'hover:-translate-y-0.5 active:translate-y-0',
  'disabled:opacity-50 disabled:pointer-events-none',
].join(' ')

function Spinner() {
  return (
    <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
  )
}

/**
 * Unified Button / Link component.
 *
 * Rendering rules (in priority order):
 *   href  → <a> anchor (external or hash links)
 *   to    → React Router <Link> (internal navigation)
 *   else  → <button> element
 *
 * Props:
 *   variant  — 'primary' | 'secondary' | 'accent' | 'whatsapp' | 'phone' |
 *              'ghost' | 'outline-white' | 'dark' | 'warm'
 *   size     — 'sm' | 'md' | 'lg' | 'xl'
 *   href     — native anchor href (renders <a>)
 *   to       — React Router path (renders <Link>)
 *   icon     — React node rendered before children
 *   loading  — shows spinner, disables interaction
 *   disabled — disables button
 */
export default function Button({
  children,
  variant  = 'primary',
  size     = 'md',
  href,
  to,
  icon,
  loading  = false,
  disabled = false,
  className = '',
  onClick,
  type,
  ...rest
}) {
  const variantClasses = (VARIANTS[variant] ?? VARIANTS.primary).join(' ')
  const classes = [BASE, variantClasses, SIZES[size] ?? SIZES.md, className].join(' ')

  const content = (
    <>
      {loading ? <Spinner /> : icon ? <span className="shrink-0">{icon}</span> : null}
      {children}
    </>
  )

  const motion_props = {
    whileHover: { scale: disabled ? 1 : 1.03 },
    whileTap:   { scale: disabled ? 1 : 0.96 },
  }

  // ── External / hash link ──────────────────────────────────────────────────
  if (href) {
    return (
      <motion.a
        href={href}
        className={classes}
        {...motion_props}
        {...rest}
      >
        {content}
      </motion.a>
    )
  }

  // ── Internal React Router link ────────────────────────────────────────────
  if (to) {
    return (
      <MotionLink
        to={to}
        className={classes}
        {...motion_props}
        {...rest}
      >
        {content}
      </MotionLink>
    )
  }

  // ── Button element ────────────────────────────────────────────────────────
  return (
    <motion.button
      type={type ?? 'button'}
      disabled={disabled || loading}
      onClick={onClick}
      className={classes}
      {...motion_props}
      {...rest}
    >
      {content}
    </motion.button>
  )
}
